import { DOCUMENT } from '@angular/common';
import { DestroyRef, inject, Injectable } from '@angular/core';
import { Meta, Title } from '@angular/platform-browser';
import { NavigationEnd, Router } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { filter } from 'rxjs';
import { environment } from '../../environments/environment';

/** What index.html ships with. */
const DEFAULT_ROBOTS = 'index, follow';

@Injectable({
  providedIn: 'root'
})
export class CanonicalService {

  #document = inject(DOCUMENT);
  #router = inject(Router);
  #destroyRef = inject(DestroyRef);
  #meta = inject(Meta);
  #title = inject(Title);
  #markedNotFound = false;

  init() {
    this.#router.events
      .pipe(
        filter(event => event instanceof NavigationEnd),
        takeUntilDestroyed(this.#destroyRef)
      )
      .subscribe((event) => {
        // Nothing else here writes robots, so the noindex from a not-found page
        // would follow the visitor to the next route.
        if (this.#markedNotFound) {
          this.#meta.updateTag({ name: 'robots', content: DEFAULT_ROBOTS });
          this.#markedNotFound = false;
        }

        this.setCanonicalUrl(
          `${environment.config.seo.baseURL}${(event as NavigationEnd).urlAfterRedirects.substring(1)}`
        );
      });
  }

  setCanonicalUrl(url: string) {
    let link: HTMLLinkElement | null = this.#document.querySelector('link[rel="canonical"]');

    if (!link) {
      link = this.#document.createElement('link');
      link.setAttribute('rel', 'canonical');
      this.#document.head.appendChild(link);
    }

    link.setAttribute('href', url);
  }

  getAbsoluteImageUrl(imageUrl: string | undefined): string {
    if (!imageUrl) return `${environment.config.seo.baseURL}assets/images/product-placeholder.webp`;
    if (imageUrl.startsWith('http://') || imageUrl.startsWith('https://')) return imageUrl;
    if (imageUrl.startsWith('/')) return `${environment.config.seo.baseURL}${imageUrl.substring(1)}`;
    return `${environment.config.seo.baseURL}${imageUrl}`;
  }

  setPageDescription(description: string) {
    this.#meta.updateTag({ name: 'description', content: description });
    this.#meta.updateTag({ property: 'og:description', content: description });
    this.#meta.updateTag({ name: 'twitter:description', content: description });
  }

  setPageImage(imageUrl: string) {
    const absoluteUrl = this.getAbsoluteImageUrl(imageUrl);
    this.#meta.updateTag({ property: 'og:image', content: absoluteUrl });
    this.#meta.updateTag({ name: 'twitter:image', content: absoluteUrl });
  }

  setPageType(type: string) {
    this.#meta.updateTag({ property: 'og:type', content: type });
  }

  setPageTitle(pageTitle: string): void {
    this.#title.setTitle(pageTitle);
    this.#meta.updateTag({ property: 'og:title', content: pageTitle });
    this.#meta.updateTag({ name: 'twitter:title', content: pageTitle });
  }

  setPageUrl(url: string): void {
    this.#meta.updateTag({ property: 'og:url', content: url });
    this.#meta.updateTag({ name: 'twitter:url', content: url });
  }

  /**
   * For a page that answers "not found" (the server sends it as 410): a
   * canonical there would point Google at a URL that shouldn't exist, which is
   * what kept the producer soft 404s alive. Nothing else on the page is touched.
   */
  markNotFound() {
    this.#document.querySelector('link[rel="canonical"]')?.remove();
    // The producer page may also add its own og:url, so there can be more than one.
    for (const selector of ['property="og:url"', 'name="twitter:url"']) {
      this.#meta.getTags(selector).forEach(tag => this.#meta.removeTagElement(tag));
    }
    this.#meta.updateTag({ name: 'robots', content: 'noindex' });
    this.#markedNotFound = true;
  }
}
